import { create } from 'zustand';
import { state as stateApi, type AppMeta } from '../api/client';
import type { Issue } from '../types/issue';
import { mergeIssueLists, parseCsvFile } from '../lib/csv';
import { formatDateTime } from '../lib/format';
import { appendSnapshot, computeSnapshotMetrics, type Snapshot } from '../lib/snapshots';
import { useFilters } from './filters';
import { useConfig } from './config';

// Der Data-Store hält die Issues + Snapshot-Meta. Filter, Modell und
// Trend werden in eigenen Hooks (src/hooks/) memoisiert, damit dieser
// Store auf die reine Daten-Verwaltung beschränkt bleibt.

type ImportResult = {
  added: number;        // Differenz Σ vor → nach Import (kann negativ sein bei replace)
  total: number;        // Σ nach Import
  total_before: number; // Σ vor Import
};

type DataState = {
  rows: Issue[];
  meta: AppMeta;
  loading: boolean;
  error: string | null;
  loadFromBackend: () => Promise<void>;
  importCsvs: (files: File[], mode: 'replace' | 'merge') => Promise<ImportResult>;
  clearAll: () => Promise<void>;
};

export const useData = create<DataState>((set, get) => ({
  rows: [],
  meta: {},
  loading: true,
  error: null,

  async loadFromBackend() {
    set({ loading: true, error: null });
    try {
      const snap = await stateApi.get();
      // SP→PT-Faktor aus dem Backend in den Filter-Store spiegeln
      useFilters.getState().set({ spToPtFactor: snap.spToPtFactor ?? 1.0 });
      set({
        rows: (snap.rows ?? []) as Issue[],
        meta: snap.meta ?? {},
        loading: false,
      });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  async importCsvs(files, mode) {
    set({ loading: true, error: null });
    try {
      const lists = await Promise.all(files.map(f => parseCsvFile(f)));
      const merged = mergeIssueLists(lists);

      const before = get().rows;
      const combined = mode === 'replace'
        ? merged
        : mergeIssueLists([before, merged]);

      const meta: AppMeta = {
        file: files.length === 1 ? files[0].name : `${files.length} CSVs`,
        sourceCount: files.length,
        importedAt: formatDateTime(new Date()),
      };

      await stateApi.saveRows(combined);
      await stateApi.saveConfig({ meta });

      // History-Snapshot anhängen — Refinement-Burn-Up baut darauf auf.
      // Stand der Konfiguration aus useConfig zur Snapshot-Zeit verwenden.
      const cfg = useConfig.getState();
      const metrics = computeSnapshotMetrics(
        combined, cfg.capacityByTeam, cfg.releaseCal,
        useFilters.getState().spToPtFactor,
      );
      const newSnap: Snapshot = {
        date: new Date().toISOString(),
        fileName: meta.file,
        importedAt: meta.importedAt,
        metrics,
      };
      const nextHistory = appendSnapshot(cfg.history, newSnap);
      await cfg.setHistory(nextHistory);

      set({ rows: combined, meta, loading: false });
      return {
        added: combined.length - before.length,
        total: combined.length,
        total_before: before.length,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ loading: false, error: msg });
      throw e;
    }
  },

  async clearAll() {
    await stateApi.saveRows([]);
    await stateApi.saveConfig({ meta: {} });
    set({ rows: [], meta: {} });
  },
}));
